import React, { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../utils/firebase';

const PayNowConfiguration = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    orgId: '',
    orgName: '',
    uen: '',
    sgqrId: '',
    bankAccountNumber: '',
    qrCodeImageUrl: '',
    apiClientId: '',
    apiClientSecret: '',
    apiKey: '',
    accessToken: '',
    apiBaseUrl: 'https://api.ocbc.com/transactional/paynowqr/1.0',
    webhookSecret: '',
    isActive: true,
    hasExistingApiCredentials: false,
    hasExistingAccessToken: false
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const clientsList = [];

      clientsSnapshot.forEach((docSnap) => {
        const clientData = docSnap.data();
        const config = clientData.paynowConfig || {};
        clientsList.push({
          orgId: docSnap.id,
          orgName: clientData.name || docSnap.id,
          uen: config.uen || '',
          sgqrId: config.sgqrId || '',
          bankAccountNumber: config.bankAccountNumber || '',
          qrCodeImageUrl: config.qrCodeImageUrl || '',
          isActive: config.isActive !== undefined ? config.isActive : false,
          hasApiCredentials: !!(config.apiClientId && config.apiClientSecret),
          hasAccessToken: !!config.accessToken
        });
      });

      setClients(clientsList);
    } catch (error) {
      console.error('Error loading PayNow configurations:', error);
      setMessage({ type: 'error', text: 'Failed to load PayNow configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = async (orgId) => {
    try {
      const clientDoc = await getDoc(doc(db, 'clients', orgId));

      if (clientDoc.exists()) {
        const clientData = clientDoc.data();
        const config = clientData.paynowConfig || {};
        const hasApiCreds = !!(config.apiClientId && config.apiClientSecret);
        const hasToken = !!config.accessToken;

        setFormData({
          orgId: orgId,
          orgName: clientData.name || orgId,
          uen: config.uen || '',
          sgqrId: config.sgqrId || '',
          bankAccountNumber: config.bankAccountNumber || '',
          qrCodeImageUrl: config.qrCodeImageUrl || '',
          apiClientId: '',
          apiClientSecret: '',
          apiKey: '',
          accessToken: '',
          apiBaseUrl: config.apiBaseUrl || 'https://api.ocbc.com/transactional/paynowqr/1.0',
          webhookSecret: '',
          isActive: config.isActive !== undefined ? config.isActive : true,
          hasExistingApiCredentials: hasApiCreds,
          hasExistingAccessToken: hasToken
        });
        setSelectedClient(orgId);
        setShowConfigForm(true);
      } else {
        setMessage({ type: 'error', text: 'Client not found' });
      }
    } catch (error) {
      console.error('Error loading client config:', error);
      setMessage({ type: 'error', text: 'Failed to load client configuration' });
    }
  };

  const handleCreateNew = () => {
    setFormData({
      orgId: '',
      orgName: '',
      uen: '',
      sgqrId: '',
      bankAccountNumber: '',
      qrCodeImageUrl: '',
      apiClientId: '',
      apiClientSecret: '',
      apiKey: '',
      accessToken: '',
      apiBaseUrl: 'https://api.ocbc.com/transactional/paynowqr/1.0',
      webhookSecret: '',
      isActive: true,
      hasExistingApiCredentials: false,
      hasExistingAccessToken: false
    });
    setSelectedClient(null);
    setShowConfigForm(true);
  };

  const handleClientPick = (e) => {
    const picked = clients.find(c => c.orgId === e.target.value);
    setFormData(prev => ({
      ...prev,
      orgId: e.target.value,
      orgName: picked ? picked.orgName : '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 2MB' });
      return;
    }

    if (!formData.orgId) {
      setMessage({ type: 'error', text: 'Select a client before uploading the QR code' });
      return;
    }

    try {
      const qrRef = ref(storage, `organizations/${formData.orgId}/paynow/sgqr-${Date.now()}`);
      await uploadBytes(qrRef, file);
      const url = await getDownloadURL(qrRef);
      setFormData(prev => ({ ...prev, qrCodeImageUrl: url }));
      setMessage({ type: 'success', text: 'QR code image uploaded successfully' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Failed to upload image' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.orgId || !formData.uen || !formData.sgqrId || !formData.bankAccountNumber) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Build the paynowConfig object
      const paynowConfig = {
        orgName: formData.orgName,
        uen: formData.uen,
        sgqrId: formData.sgqrId,
        bankAccountNumber: formData.bankAccountNumber,
        qrCodeImageUrl: formData.qrCodeImageUrl,
        apiBaseUrl: formData.apiBaseUrl,
        isActive: formData.isActive,
        updatedAt: serverTimestamp()
      };

      // Only include API credentials if new values were provided
      if (formData.apiClientId) {
        paynowConfig.apiClientId = formData.apiClientId;
      }
      if (formData.apiClientSecret) {
        paynowConfig.apiClientSecret = formData.apiClientSecret;
      }
      if (formData.apiKey) {
        paynowConfig.apiKey = formData.apiKey;
      }
      if (formData.accessToken) {
        paynowConfig.accessToken = formData.accessToken;
      }
      if (formData.webhookSecret) {
        paynowConfig.webhookSecret = formData.webhookSecret;
      }

      // If editing and keeping existing credentials, preserve them
      if (selectedClient && (formData.hasExistingApiCredentials || formData.hasExistingAccessToken)) {
        const existingDoc = await getDoc(doc(db, 'clients', formData.orgId));
        if (existingDoc.exists()) {
          const existingConfig = existingDoc.data().paynowConfig || {};
          if (!formData.apiClientId && existingConfig.apiClientId) {
            paynowConfig.apiClientId = existingConfig.apiClientId;
          }
          if (!formData.apiClientSecret && existingConfig.apiClientSecret) {
            paynowConfig.apiClientSecret = existingConfig.apiClientSecret;
          }
          if (!formData.apiKey && existingConfig.apiKey) {
            paynowConfig.apiKey = existingConfig.apiKey;
          }
          if (!formData.accessToken && existingConfig.accessToken) {
            paynowConfig.accessToken = existingConfig.accessToken;
          }
          if (!formData.webhookSecret && existingConfig.webhookSecret) {
            paynowConfig.webhookSecret = existingConfig.webhookSecret;
          }
        }
      }

      // Update the client document with the paynowConfig
      const clientRef = doc(db, 'clients', formData.orgId);
      await updateDoc(clientRef, {
        paynowConfig: paynowConfig,
        updatedAt: serverTimestamp()
      });

      setMessage({ type: 'success', text: 'PayNow configuration saved successfully' });
      setShowConfigForm(false);
      loadClients();
    } catch (error) {
      console.error('Error saving PayNow config:', error);
      setMessage({ type: 'error', text: 'Failed to save PayNow configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowConfigForm(false);
    setSelectedClient(null);
    setMessage({ type: '', text: '' });
  };

  if (loading) {
    return <div className="loading-spinner">Loading PayNow configurations...</div>;
  }

  return (
    <div className="paynow-configuration">
      <div className="paynow-header">
        <div>
          <h2>PayNow Configuration</h2>
          <p className="paynow-subtitle">Configure SGQR and PayNow settings for each organisation</p>
        </div>
        <button className="btn-primary" onClick={handleCreateNew}>
          + Add New Configuration
        </button>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}

      {!showConfigForm ? (
        <div className="paynow-list">
          {clients.length === 0 ? (
            <div className="empty-state">
              <p>No PayNow configurations found</p>
              <p className="empty-state-hint">Click "Add New Configuration" to get started</p>
            </div>
          ) : (
            <div className="config-cards">
              {clients.map(client => (
                <div key={client.orgId} className="config-card">
                  <div className="config-card-header">
                    <h3>{client.orgName || client.orgId}</h3>
                    <span className={`status-badge ${client.isActive ? 'active' : 'inactive'}`}>
                      {client.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="config-card-body">
                    <div className="config-field">
                      <label>Org ID:</label>
                      <span>{client.orgId}</span>
                    </div>
                    <div className="config-field">
                      <label>UEN:</label>
                      <span>{client.uen}</span>
                    </div>
                    <div className="config-field">
                      <label>SGQR ID:</label>
                      <span>{client.sgqrId}</span>
                    </div>
                    <div className="config-field">
                      <label>Bank Account:</label>
                      <span>{client.bankAccountNumber}</span>
                    </div>
                    <div className="config-field">
                      <label>QR Code:</label>
                      <span>{client.qrCodeImageUrl ? 'Uploaded' : 'Not uploaded'}</span>
                    </div>
                    <div className="config-field">
                      <label>API Configured:</label>
                      <span>{client.hasApiCredentials ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="config-field">
                      <label>Access Token:</label>
                      <span>{client.hasAccessToken ? 'Configured' : 'Not set'}</span>
                    </div>
                  </div>
                  <div className="config-card-footer">
                    <button
                      className="btn-secondary"
                      onClick={() => handleEditConfig(client.orgId)}
                    >
                      Edit Configuration
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="paynow-form-container">
          <form onSubmit={handleSubmit} className="paynow-form">
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Org ID *</label>
                  {selectedClient ? (
                    <input type="text" name="orgId" value={formData.orgId} disabled />
                  ) : (
                    <select name="orgId" value={formData.orgId} onChange={handleClientPick} required>
                      <option value="">Select a client…</option>
                      {clients.map(c => (
                        <option key={c.orgId} value={c.orgId}>{c.orgName} ({c.orgId})</option>
                      ))}
                    </select>
                  )}
                  <small>Unique identifier for the organisation</small>
                </div>
                <div className="form-group">
                  <label>Organisation Name</label>
                  <input
                    type="text"
                    name="orgName"
                    value={formData.orgName}
                    onChange={handleInputChange}
                    placeholder="e.g., WiCare"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>PayNow Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>UEN (Unique Entity Number) *</label>
                  <input
                    type="text"
                    name="uen"
                    value={formData.uen}
                    onChange={handleInputChange}
                    placeholder="e.g., 201234567A"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SGQR ID *</label>
                  <input
                    type="text"
                    name="sgqrId"
                    value={formData.sgqrId}
                    onChange={handleInputChange}
                    placeholder="Your SGQR ID"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Account Number *</label>
                  <input
                    type="text"
                    name="bankAccountNumber"
                    value={formData.bankAccountNumber}
                    onChange={handleInputChange}
                    placeholder="Your OCBC account number"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>QR Code Image</h3>
              <div className="form-group">
                <label>Upload SGQR Code Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <small>Upload your SGQR code image (PNG, JPG, max 2MB)</small>
                {formData.qrCodeImageUrl && (
                  <div className="qr-preview">
                    <img src={formData.qrCodeImageUrl} alt="QR Code Preview" />
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>OCBC API Configuration</h3>
              <p className="section-description">
                Configure your OCBC Corporate PayNow API credentials to enable dynamic QR code generation with pre-filled amounts.
                {(formData.hasExistingApiCredentials || formData.hasExistingAccessToken) && (
                  <strong> Some credentials are already configured. Leave fields blank to keep existing values, or enter new values to update them.</strong>
                )}
                {!formData.hasExistingApiCredentials && !formData.hasExistingAccessToken && ' Fill in the Access Token to enable dynamic QR codes.'}
              </p>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Access Token (Bearer) {formData.hasExistingAccessToken && <span className="existing-indicator">Configured</span>}</label>
                  <input
                    type="password"
                    name="accessToken"
                    value={formData.accessToken}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingAccessToken ? "Leave blank to keep existing token" : "Paste your OCBC API Bearer token here"}
                  />
                  <small>The Bearer token from your OCBC Sandbox/Production application. Required for dynamic QR code generation.</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>API Client ID {formData.hasExistingApiCredentials && <span className="existing-indicator">Configured</span>}</label>
                  <input
                    type="text"
                    name="apiClientId"
                    value={formData.apiClientId}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Your OCBC API Client ID"}
                  />
                </div>
                <div className="form-group">
                  <label>API Client Secret {formData.hasExistingApiCredentials && <span className="existing-indicator">Configured</span>}</label>
                  <input
                    type="password"
                    name="apiClientSecret"
                    value={formData.apiClientSecret}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Your OCBC API Client Secret"}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>API Key</label>
                  <input
                    type="password"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Your OCBC API Key (if applicable)"}
                  />
                </div>
                <div className="form-group">
                  <label>API Base URL</label>
                  <input
                    type="text"
                    name="apiBaseUrl"
                    value={formData.apiBaseUrl}
                    onChange={handleInputChange}
                    placeholder="https://api.ocbc.com/transactional/paynowqr/1.0"
                  />
                  <small>Default: https://api.ocbc.com/transactional/paynowqr/1.0</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Webhook Secret</label>
                  <input
                    type="password"
                    name="webhookSecret"
                    value={formData.webhookSecret}
                    onChange={handleInputChange}
                    placeholder={formData.hasExistingApiCredentials ? "Leave blank to keep existing value" : "Secret key for webhook verification"}
                  />
                  <small>Used to verify webhook requests from OCBC (if applicable)</small>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span>Enable PayNow for this organisation</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : selectedClient ? 'Update Configuration' : 'Create Configuration'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PayNowConfiguration;
